/**
 * Handles showing a confirm dialog when performing a destructive action that requires confirmation (such as unlinking, reconnecting or removing an account).
 */

const SELECTORS = {
  confirmDialog: '.js-confirm-dialog',
  confirmDialogTitle: '.js-confirm-dialog-title',
  confirmDialogContent: '.js-confirm-dialog-content',
  confirmBtn: '.js-confirm-dialog-confirm-button',
  cancelBtn: '.js-confirm-dialog-cancel-button',
  destructiveActions: '.js-confirm-dialog-confirmation-target',
  primaryAccount: '.js-primary-account',
  accountMigrationAction: '.js-confirm-dialog-confirmation-target[data-action="link"]'
};

const dom = {
  $confirmDialog: document.querySelector<HTMLDialogElement>(SELECTORS.confirmDialog),
  $confirmDialogTitle: document.querySelector<HTMLDialogElement>(SELECTORS.confirmDialogTitle),
  $confirmDialogContent: document.querySelector(SELECTORS.confirmDialogContent),
  $confirmBtn: document.querySelector(SELECTORS.confirmBtn),
  $cancelBtn: document.querySelector(SELECTORS.cancelBtn),
  $destructiveActions: document.querySelectorAll(SELECTORS.destructiveActions),
  $accountMigrationAction: document.querySelector(SELECTORS.accountMigrationAction)
}

const instance = ($el: HTMLElement) => {
  $el.addEventListener('click', handleButtonClick);
}

const handleButtonClick = (e: MouseEvent) => {
  e.preventDefault();
  if (!(e.target instanceof HTMLElement)) {
    return;
  }
  if (!dom.$confirmDialogContent || !dom.$confirmDialogTitle) {
    return;
  }

  const categoryToPlatform = new Map([
    ['xbox', 'Xbox'],
    ['psn', 'Sony'],
    ['steam', 'Steam'],
    ['gog', 'GOG'],
    ['nintendo', 'Nintendo'],
    ['twitch', 'Twitch'],]);

  // If the click is taking event on a Form, then use the form as the target element, otherwise use the clicked element (to support both anchor and form elements))
  const $el = e.target.closest('form') ? e.target.closest('form') : e.target;

  const $clickBeingConfirmed = $el!.cloneNode(true);
  const action = $el!.getAttribute('data-action');
  const platform = categoryToPlatform.get($el!.getAttribute('data-category') ?? '');
  const username = $el!.getAttribute('data-migration-username');
  const isDestructiveMigration = $el!.getAttribute('data-migration-only-platform')?.toLowerCase() === 'true';

  let confirmDialogTitle = '';
  let confirmDialogContent = '';
  let confirmDialogButtonText = '';

  if (action === 'remove') {
    confirmDialogTitle = 'Disconnect All Accounts';
    confirmDialogContent = 'Disconnecting all your connected accounts will permanently erase your "Hello User ID", any No Man\'s Sky alliances you have created, and any save data you have stored on the Hello Games servers. <br/> Are you sure you want to continue and erase your cross-platform data?';
    confirmDialogButtonText = 'ERASE DATA';
  }

  if (action === 'unlink') {
    confirmDialogTitle = 'Disconnect Account';
    confirmDialogContent = `Are you sure you want to disconnect your ${platform} account?`;
    confirmDialogButtonText = 'DISCONNECT'
  }

  if (action === 'link') {
    confirmDialogTitle = 'Reconnect Account';
    confirmDialogButtonText = `RECONNECT`;

    // If the user decides to cancel the reconnect dialog, we want the connect button to behave as normal and allow them to connect to a new account
    // (rather than trigger the warning dialog again in order to re-attempt the same account migration)
    $el?.removeEventListener('click', handleButtonClick);

    confirmDialogContent = `Warning: Your ${platform} account ${username} is already connected to another "Hello User ID".<br/>
    If you would like to move this account to your current Hello User ID, please click the ${confirmDialogButtonText} button below.<br/><br/>`;

    if (isDestructiveMigration) {
      confirmDialogContent += `Note that the other Hello User ID, and any saves uploaded to its cross-platform storage, will be permanently deleted if you do this.<br/><br/>
      Are you sure you want to continue?`;
    }
    else {
      confirmDialogContent += `Note that your ${platform} account ${username} will no longer have access to the cross-platform storage or alliances associated with the other Hello User ID if you do this.<br/><br/>
      Are you sure you want to continue?`;
    }
  }

  dom.$confirmDialogTitle.innerText = confirmDialogTitle;
  dom.$confirmDialogContent.innerHTML = confirmDialogContent;

  // If we're confirming an anchor element click (a GET), then place the anchor element within the confirm button
  if ($clickBeingConfirmed instanceof HTMLAnchorElement) {
    $clickBeingConfirmed.innerText = confirmDialogButtonText;
    // make sure styles from the element being confirmed aren't carried over to the confirm dialog
    $clickBeingConfirmed.setAttribute("class", "");
    dom.$confirmBtn?.replaceChildren($clickBeingConfirmed);
    // If we're confirming a form element click (a POST), then just submit the form when the confirm button is clicked
  } else if ($clickBeingConfirmed instanceof HTMLFormElement) {
    dom.$confirmBtn!.innerHTML = confirmDialogButtonText;
    dom.$confirmBtn?.addEventListener('click', () => {
      const formElement = $el as HTMLFormElement;
      if (action === "link") {
        // update the form action to point to the migration endpoint
        formElement.action = formElement.action.toLowerCase().replace('/signin/', '/migrate/');
      }
      formElement.submit()
    });
  }

  dom.$confirmDialog?.showModal();
};

export default () => {
  if (dom.$confirmDialog === null) {
    return;
  }
  dom.$cancelBtn?.addEventListener('click', () => dom.$confirmDialog?.close());
  dom.$destructiveActions.forEach(($el: Element) => {
    if ($el instanceof HTMLElement) {
      instance($el);
    }
  });

  // If there is an account migration action present on page load, automatically activate the warning dialog associated with it
  if (dom.$accountMigrationAction) {
    (dom.$accountMigrationAction as HTMLElement).click();
  }
};
